import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  can,
  type AssistantAliasDto,
  type AssistantLinkedPhoneDto,
  type AssistantNoteDto,
  type AssistantOutcome,
  type AssistantSettingsDto,
  type AssistantTier,
  type AssistantTurnDto,
} from '@pl/domain';
import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useSearchParams } from 'react-router';
import { ApiError, api } from '../lib/api';
import { skuName, zoneName, zonedDateTime } from '../lib/format';
import {
  qk,
  useAssistantAliases,
  useAssistantLinkedPhones,
  useAssistantNotes,
  useAssistantSettings,
  useAssistantStatus,
  useAssistantTurns,
  useMe,
  useOrg,
  useSites,
  useSkus,
  useUsers,
  type TurnFilters,
} from '../lib/queries';
import { Chip, Empty, ErrorNote, RefetchNote, Spinner } from '../ui/bits';
import { Modal } from '../ui/Dialog';
import s from './AssistantAdmin.module.css';

type Tab = 'notes' | 'vocabulary' | 'conversations' | 'phones' | 'number';
const TABS: { id: Tab; label: string }[] = [
  { id: 'notes', label: 'Notes' },
  { id: 'vocabulary', label: 'Vocabulary' },
  { id: 'conversations', label: 'Conversations' },
  { id: 'phones', label: 'Linked phones' },
  { id: 'number', label: 'WhatsApp number' },
];
/** When a vocabulary change reaches the answers: the next rebuild, which the registry runs 30 s after it notices. */
const NEXT_REBUILD = 'from its next rebuild, about 30 seconds';
const pct = (x: number | null) => (x === null ? '—' : `${Math.round(x * 100)} %`);

/**
 * The company's clock: the zone this page reads times and days in, the company's own (`GET /org`, set in
 * Settings; UTC until someone sets it). Undefined while it loads.
 */
function useCompanyZone(): string | undefined {
  const org = useOrg();
  if (org.isPending) return undefined;
  return org.data?.timezone ?? 'UTC';
}

/**
 * The people the Conversations filter offers: the company's own (`GET /users`, admins only, as this page
 * is), sorted by name, whether or not the log has them yet. Deactivated people stay, since their turns
 * do, and say so.
 */
function usePeople(): { id: string; name: string }[] {
  const users = useUsers();
  return useMemo(
    () => (users.data ?? []).map((u) => ({ id: u.id, name: u.active ? u.name : `${u.name} (deactivated)` })).sort((a, b) => a.name.localeCompare(b.name)),
    [users.data],
  );
}

/** A refused request in words: the server's own reason for each refused field, or the error's message. */
function problemOf(error: unknown): string {
  if (error instanceof ApiError && Array.isArray(error.details)) {
    const reasons = error.details.flatMap((d: unknown) => (d && typeof d === 'object' && 'message' in d ? [String(d.message)] : []));
    if (reasons.length) return reasons.join(' ');
  }
  return error instanceof Error ? error.message : 'Something went wrong';
}

/** A first load that failed: nothing to show, so the error and a way to ask again. */
function LoadFailed({ error, retry }: { error: unknown; retry: () => void }) {
  return (
    <div className={s.failed}>
      <ErrorNote error={error} />
      <button type="button" className="btn sm" onClick={retry}>
        Try again
      </button>
    </div>
  );
}

/**
 * Office → Assistant (spec §7.4, §7.5, §8.1, §15.1; Task 12): the admin's page for running the
 * assistant. Admins only (ruling 1): the sidebar offers it to `settings` holders alone, anyone else who
 * opens the address sees "Admins only", and nothing here is asked of the API for them.
 */
export function AssistantAdmin() {
  const me = useMe();
  if (!me.data) return null;
  if (!can(me.data.role, 'settings')) return <Empty title="Admins only" description="The assistant’s notes, vocabulary, conversations, linked phones and number are for admins." />;
  return <AdminPage />;
}

function AdminPage() {
  const zone = useCompanyZone();
  const [params, setParams] = useSearchParams();
  const tab = TABS.find((x) => x.id === params.get('tab'))?.id ?? 'notes';
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const select = (id: Tab) => setParams(id === 'notes' ? {} : { tab: id }, { replace: true });
  const onTabKey = (e: KeyboardEvent) => {
    const i = TABS.findIndex((x) => x.id === tab);
    const to = e.key === 'ArrowRight' ? (i + 1) % TABS.length : e.key === 'ArrowLeft' ? (i - 1 + TABS.length) % TABS.length : e.key === 'Home' ? 0 : e.key === 'End' ? TABS.length - 1 : -1;
    if (to < 0) return;
    e.preventDefault();
    select(TABS[to]!.id);
    tabRefs.current[TABS[to]!.id]?.focus();
  };
  // Every time here is on the company's clock, so nothing shows until it is known: a status that
  // arrived first would otherwise be written in the viewer's own zone.
  if (!zone) return <Spinner label="Loading the company’s clock" />;
  return (
    <div className={s.page}>
      <StatusPanel zone={zone} />
      <div className={`tabs ${s.tabs}`} role="tablist" aria-label="Assistant" onKeyDown={onTabKey}>
        {TABS.map((x) => (
          <button
            key={x.id}
            ref={(el) => {
              tabRefs.current[x.id] = el;
            }}
            id={`assistant-tab-${x.id}`}
            type="button"
            role="tab"
            aria-selected={tab === x.id}
            aria-controls={tab === x.id ? 'assistant-panel' : undefined}
            tabIndex={tab === x.id ? 0 : -1}
            className={tab === x.id ? 'on' : undefined}
            onClick={() => select(x.id)}
          >
            {x.label}
          </button>
        ))}
      </div>
      <div id="assistant-panel" role="tabpanel" aria-labelledby={`assistant-tab-${tab}`} className={s.panel}>
        {tab === 'notes' ? (
          <NotesTab zone={zone} />
        ) : tab === 'vocabulary' ? (
          <VocabularyTab zone={zone} />
        ) : tab === 'conversations' ? (
          <ConversationsTab zone={zone} />
        ) : tab === 'phones' ? (
          <PhonesTab zone={zone} />
        ) : (
          <NumberTab />
        )}
      </div>
    </div>
  );
}

/** The live build and the last failure, in the API's words (ruling 5; spec §8.1: admins see why). */
function StatusPanel({ zone }: { zone: string }) {
  const st = useAssistantStatus();
  if (st.isPending) return <Spinner label="Loading the assistant’s status" />;
  if (st.isLoadingError) return <LoadFailed error={st.error} retry={() => void st.refetch()} />;
  const d = st.data!;
  return (
    <section className={`panel ${s.status}`} aria-label="Assistant status">
      <p className={s.line}>
        {d.builtAt
          ? `Model built ${zonedDateTime(d.builtAt, zone)} · seed ${d.seed} · intent ${pct(d.intentAcc)} · slots ${pct(d.slotExact)} · LLM tier ${d.llm ? 'on' : 'off'}`
          : 'Not built yet.'}
      </p>
      {d.builtAt && (
        <p className={s.muted}>
          Intent and slots: how often the model, on test questions kept out of its training, picked the right kind of question and read its details right. A model is only used at 90 % and 80 % or
          better.
        </p>
      )}
      {d.rebuildScheduled && <p className={s.muted}>Rebuilding with the latest changes. This takes about 30 seconds.</p>}
      {d.lastError && (
        <p role="alert" className="error-note">
          {`The last rebuild failed${d.lastErrorAt ? ` (${zonedDateTime(d.lastErrorAt, zone)})` : ''}: ${d.lastError}${d.builtAt ? ' The previous model is still answering.' : ''}`}
        </p>
      )}
      <RefetchNote queries={[st]} />
    </section>
  );
}

const clip = (text: string, n = 60) => (text.length > n ? `${text.slice(0, n - 1)}…` : text);

function NotesTab({ zone }: { zone: string }) {
  const qc = useQueryClient();
  const notes = useAssistantNotes();
  const [body, setBody] = useState('');
  const countId = useId();
  const refresh = () => qc.invalidateQueries({ queryKey: qk.assistantNotes });
  const add = useMutation({
    mutationFn: (text: string) => api.post<AssistantNoteDto>('/assistant/notes', { body: text }),
    onSuccess: (_note, text) => {
      // Only what was saved: a note typed while this one was saving stays.
      setBody((b) => (b.trim() === text ? '' : b));
      void refresh();
    },
  });
  const toggle = useMutation({ mutationFn: (n: { id: string; active: boolean }) => api.patch<AssistantNoteDto>(`/assistant/notes/${n.id}`, { active: n.active }), onSuccess: refresh });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (body.trim()) add.mutate(body.trim());
  };
  return (
    <section className={s.section} aria-label="Notes">
      <p className={s.rule}>
        <strong>Anyone who asks can read these. Never put stock figures in a note.</strong> Notes are for how the warehouse works: hours, carriers, where things are kept. Staff who ask “any notes
        about …” get them back word for word.
      </p>
      <form onSubmit={submit} className={s.row}>
        <label className={s.grow}>
          <span className="caps">New note</span>
          <textarea
            className="inp"
            maxLength={500}
            rows={2}
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
              if (add.isError) add.reset();
            }}
            placeholder="Maryland dock closes at 4 pm"
            aria-describedby={countId}
          />
          <small id={countId}>{body.length} of 500 characters</small>
        </label>
        <button type="submit" className="btn primary" disabled={add.isPending || !body.trim()}>
          Add note
        </button>
      </form>
      <ErrorNote error={add.error} />
      {notes.isPending ? (
        <Spinner label="Loading notes" />
      ) : notes.isLoadingError ? (
        <LoadFailed error={notes.error} retry={() => void notes.refetch()} />
      ) : notes.data!.length === 0 ? (
        <Empty title="No notes yet" description="A note added here is what the assistant reads out." />
      ) : (
        <ul className={s.list}>
          {notes.data!.map((n) => (
            <li key={n.id} className={n.active ? s.item : `${s.item} ${s.off}`}>
              <span className={s.grow}>
                <b>{n.body}</b>
                <small>{`${n.createdByName} · ${zonedDateTime(n.updatedAt, zone)}${n.active ? '' : ' · hidden'}`}</small>
              </span>
              <button
                type="button"
                className="btn sm"
                onClick={() => toggle.mutate({ id: n.id, active: !n.active })}
                disabled={toggle.isPending}
                aria-label={`${n.active ? 'Hide' : 'Show'} note: ${clip(n.body)}`}
              >
                {n.active ? 'Hide' : 'Show'}
              </button>
            </li>
          ))}
        </ul>
      )}
      <ErrorNote error={toggle.error} />
      <RefetchNote queries={[notes]} />
    </section>
  );
}

type Review = { kind: 'approve' | 'reject' | 'remove'; alias: AssistantAliasDto };

/** What a review will do, said before it is done (ruling 3). */
function reviewWords(r: Review): { title: string; body: string; button: string } {
  const { phrase, targetLabel, action } = r.alias;
  if (r.kind === 'approve')
    return {
      title: `Approve “${phrase}”?`,
      body:
        action === 'add'
          ? `The assistant will understand “${phrase}” as ${targetLabel} ${NEXT_REBUILD}.`
          : `The assistant will stop reading “${phrase}” as ${targetLabel} ${NEXT_REBUILD}.`,
      button: 'Approve',
    };
  if (r.kind === 'reject') return { title: `Reject “${phrase}”?`, body: `The assistant will not learn “${phrase}”, and will not suggest it again.`, button: 'Reject' };
  return {
    title: r.alias.source === 'admin' ? `Remove the rule “${phrase}”?` : `Remove “${phrase}”?`,
    body: `The assistant stops using it ${NEXT_REBUILD}.`,
    button: 'Remove',
  };
}

function VocabularyTab({ zone }: { zone: string }) {
  const qc = useQueryClient();
  const pending = useAssistantAliases('pending');
  const rules = useAssistantAliases('active', 'admin');
  const approved = useAssistantAliases('active', 'learned');
  const skus = useSkus();
  const sites = useSites();
  const [kind, setKind] = useState<'sku' | 'site'>('sku');
  const [targetId, setTargetId] = useState('');
  const [phrase, setPhrase] = useState('');
  const [action, setAction] = useState<'add' | 'remove'>('add');
  const [added, setAdded] = useState(false);
  const [confirming, setConfirming] = useState<Review | null>(null);
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['assistant-aliases'] });
    // Reading the status is what notices the change and schedules the rebuild (registry.get).
    await qc.invalidateQueries({ queryKey: qk.assistantStatus });
  };
  const review = useMutation({
    mutationFn: (r: Review) => api.post<AssistantAliasDto>(`/assistant/aliases/${r.alias.id}/${r.kind === 'approve' ? 'approve' : 'reject'}`),
    onSuccess: async () => {
      setConfirming(null);
      await refresh();
    },
  });
  const add = useMutation({
    mutationFn: (words: string) => api.post<AssistantAliasDto>('/assistant/aliases', { targetKind: kind, targetId, phrase: words, action }),
    onSuccess: async (_rule, words) => {
      // Only what was added: words typed while it was saving stay.
      setPhrase((p) => (p.trim() === words ? '' : p));
      setAdded(true);
      await refresh();
    },
  });
  const edited = () => {
    setAdded(false);
    if (add.isError) add.reset();
  };
  const closeConfirm = () => {
    setConfirming(null);
    review.reset();
  };
  const targets =
    kind === 'sku' ? (skus.data ?? []).map((k) => ({ id: k.id, label: `${skuName(k)} (${k.code})` })) : (sites.data ?? []).map((x) => ({ id: x.id, label: x.name }));
  const inUse = [...(rules.data ?? []), ...(approved.data ?? [])];
  const words = confirming && reviewWords(confirming);

  return (
    <section className={s.section} aria-label="Vocabulary">
      <h2 className={s.h}>Waiting for review</h2>
      <p className={s.muted}>Words people used when the assistant had to ask which product they meant. Nothing here is used until you approve it.</p>
      {pending.isPending ? (
        <Spinner label="Loading words to review" />
      ) : pending.isLoadingError ? (
        <LoadFailed error={pending.error} retry={() => void pending.refetch()} />
      ) : pending.data!.length === 0 ? (
        <p className={s.muted}>Nothing to review.</p>
      ) : (
        <ul className={s.list}>
          {pending.data!.map((a) => (
            <li key={a.id} className={s.item}>
              <span className={s.grow}>
                <span>
                  <b>“{a.phrase}”</b> → {a.targetLabel}
                </span>
                <small>{`${a.createdByName ? `From ${a.createdByName}` : 'Learned'} · ${zonedDateTime(a.createdAt, zone)}`}</small>
              </span>
              <button type="button" className="btn sm primary" onClick={() => setConfirming({ kind: 'approve', alias: a })} aria-label={`Approve “${a.phrase}”`}>
                Approve
              </button>
              <button type="button" className="btn sm" onClick={() => setConfirming({ kind: 'reject', alias: a })} aria-label={`Reject “${a.phrase}”`}>
                Reject
              </button>
            </li>
          ))}
        </ul>
      )}
      <RefetchNote queries={[pending]} />

      <h2 className={s.h}>Rules</h2>
      <p className={s.muted}>Tell the assistant what words mean in this company, or what they do not.</p>
      <form
        className={s.row}
        onSubmit={(e) => {
          e.preventDefault();
          if (targetId && phrase.trim()) add.mutate(phrase.trim());
        }}
      >
        <label>
          <span className="caps">Words</span>
          <input
            className="inp"
            value={phrase}
            maxLength={200}
            onChange={(e) => {
              setPhrase(e.target.value);
              edited();
            }}
            placeholder="cat6 gray"
          />
        </label>
        <label>
          <span className="caps">Rule</span>
          <select
            className="inp"
            value={action}
            onChange={(e) => {
              setAction(e.target.value as 'add' | 'remove');
              edited();
            }}
          >
            <option value="add">mean</option>
            <option value="remove">do not mean</option>
          </select>
        </label>
        <label>
          <span className="caps">Kind</span>
          <select
            className="inp"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as 'sku' | 'site');
              setTargetId('');
              edited();
            }}
          >
            <option value="sku">Product</option>
            <option value="site">Site</option>
          </select>
        </label>
        <label className={s.grow}>
          <span className="caps">Target</span>
          <select
            className="inp"
            value={targetId}
            onChange={(e) => {
              setTargetId(e.target.value);
              edited();
            }}
          >
            <option value="">Choose…</option>
            {targets.map((x) => (
              <option key={x.id} value={x.id}>
                {x.label}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="btn primary" disabled={add.isPending || !targetId || !phrase.trim()}>
          Add rule
        </button>
      </form>
      {add.isError && (
        <p role="alert" className="error-note">
          {problemOf(add.error)}
        </p>
      )}
      {added && (
        <p role="status" className={s.ok}>
          Added. The assistant uses it {NEXT_REBUILD}.
        </p>
      )}

      <h2 className={s.h}>In use</h2>
      {rules.isPending || approved.isPending ? (
        <Spinner label="Loading rules" />
      ) : rules.isLoadingError || approved.isLoadingError ? (
        <LoadFailed
          error={rules.error ?? approved.error}
          retry={() => {
            void rules.refetch();
            void approved.refetch();
          }}
        />
      ) : inUse.length === 0 ? (
        <p className={s.muted}>No rules or approved words yet.</p>
      ) : (
        <ul className={s.list}>
          {inUse.map((a) => (
            <li key={a.id} className={s.item}>
              <span className={s.grow}>
                <span>
                  <b>“{a.phrase}”</b> {a.action === 'add' ? 'means' : 'does not mean'} {a.targetLabel}
                </span>
                <small>
                  <span>{`${a.hits} ${a.hits === 1 ? 'use' : 'uses'}`}</span>
                  {` · ${a.source === 'admin' ? 'rule' : 'approved'}${a.createdByName ? `, ${a.createdByName}` : ''}`}
                </small>
              </span>
              <button
                type="button"
                className="btn sm"
                onClick={() => setConfirming({ kind: 'remove', alias: a })}
                aria-label={a.source === 'admin' ? `Remove rule “${a.phrase}”` : `Remove “${a.phrase}”`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      <RefetchNote queries={[rules, approved]} />

      <Modal
        open={confirming !== null}
        onOpenChange={(open) => (open ? undefined : closeConfirm())}
        title={words?.title ?? ''}
        footer={
          <>
            <button type="button" className="btn" onClick={closeConfirm}>
              Cancel
            </button>
            <button type="button" className="btn primary" onClick={() => confirming && review.mutate(confirming)} disabled={review.isPending}>
              {words?.button}
            </button>
          </>
        }
      >
        <p className={s.confirmText}>{words?.body}</p>
        <ErrorNote error={review.error} />
      </Modal>
    </section>
  );
}

const OUTCOMES: { id: AssistantOutcome; label: string; tone?: 'ok' | 'warn' | 'bad' }[] = [
  { id: 'answered', label: 'Answered', tone: 'ok' },
  { id: 'asked', label: 'Asked back' },
  { id: 'unknown', label: 'Not understood', tone: 'warn' },
  { id: 'error', label: 'Error', tone: 'bad' },
];
const TIERS: Record<AssistantTier, string> = { template: 'template', net: 'model', llm: 'LLM', followup: 'follow-up' };
const day = (ymd: string) => new Date(`${ymd}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

/** "today", "on Sep 21", "from Sep 20 to Sep 22": the period slot, "2026-09-20..2026-09-22". */
function periodWords(period: string | undefined): string {
  if (!period) return 'today';
  const [from, to] = period.split('..');
  if (!from || !to) return period;
  return from === to ? `on ${day(from)}` : `from ${day(from)} to ${day(to)}`;
}

/**
 * How the assistant read a question (ruling 2): the tool it chose and the slots it filled, in the words
 * the assistant's own "I read that as" uses (replies.ts `readingOf`), so an admin can spot a wrong reading.
 */
export function readingOf(t: Pick<AssistantTurnDto, 'tool' | 'slots'>, names: { sku: (code: string) => string | undefined; site: (code: string) => string | undefined }): string | null {
  if (!t.tool) return null;
  const sku = t.slots.sku ? (names.sku(t.slots.sku) ?? t.slots.sku) : undefined;
  const site = t.slots.site ? (names.site(t.slots.site) ?? t.slots.site) : undefined;
  const where = site ? ` at ${site}` : ' at every site they can see';
  switch (t.tool) {
    case 'stockLevel':
      return sku ? `stock of ${sku}${where}` : 'a stock question, without the product';
    case 'lowStock':
      return `low stock${where}`;
    case 'recentActivity':
      return `activity ${periodWords(t.slots.period)}${where}`;
    case 'transferStatus':
      return site ? `transfers on their way to or from ${site}` : 'transfers on their way';
    case 'orderStatus':
      return t.slots.order ? `the status of order ${t.slots.order}` : 'an order question, without the order number';
    case 'boxHistory':
      return t.slots.box ? `where box ${t.slots.box} is` : 'a box question, without the box code';
    case 'notes':
      return t.slots.topic ? `the notes about “${t.slots.topic}”` : 'the notes';
    default:
      return t.tool;
  }
}

function ConversationsTab({ zone }: { zone: string }) {
  const [filters, setFilters] = useState<Omit<TurnFilters, 'tz'>>({});
  const turns = useAssistantTurns({ ...filters, tz: zone });
  const skus = useSkus({ includeInactive: true });
  const sites = useSites();
  const rows = useMemo(() => turns.data?.pages.flat() ?? [], [turns.data]);
  const people = usePeople();
  const names = useMemo(() => {
    const bySku = new Map((skus.data ?? []).map((k) => [k.code, skuName(k)]));
    const bySite = new Map((sites.data ?? []).map((x) => [x.shortCode, x.name]));
    return { sku: (code: string) => bySku.get(code), site: (code: string) => bySite.get(code) };
  }, [skus.data, sites.data]);
  const filtered = Boolean(filters.userId || filters.outcome || filters.day);
  const set = (patch: Partial<Omit<TurnFilters, 'tz'>>) => setFilters((f) => Object.fromEntries(Object.entries({ ...f, ...patch }).filter(([, v]) => v)) as Omit<TurnFilters, 'tz'>);

  return (
    <section className={s.section} aria-label="Conversations">
      <p className={s.muted}>What people asked the assistant and what it answered, kept 90 days. Link codes and keys are never kept, and phone numbers are masked.</p>
      <div className={s.row} role="group" aria-label="Filter conversations">
        <label>
          <span className="caps">Person</span>
          <select className="inp" value={filters.userId ?? ''} onChange={(e) => set({ userId: e.target.value || undefined })}>
            <option value="">Everyone</option>
            {people.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="caps">Outcome</span>
          <select className="inp" value={filters.outcome ?? ''} onChange={(e) => set({ outcome: (e.target.value || undefined) as AssistantOutcome | undefined })}>
            <option value="">All</option>
            {OUTCOMES.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="caps">{`Day (${zoneName(new Date(), zone)})`}</span>
          <input type="date" className="inp" value={filters.day ?? ''} onChange={(e) => set({ day: e.target.value || undefined })} />
        </label>
        {filtered && (
          <button type="button" className="btn sm" onClick={() => setFilters({})}>
            Clear filters
          </button>
        )}
      </div>
      {turns.isPending ? (
        <Spinner label="Loading conversations" />
      ) : turns.isLoadingError ? (
        <LoadFailed error={turns.error} retry={() => void turns.refetch()} />
      ) : rows.length === 0 ? (
        <Empty
          title={filtered ? 'No questions match these filters' : 'No questions yet'}
          description={filtered ? undefined : 'Questions asked on WhatsApp and in the app appear here for 90 days.'}
        />
      ) : (
        <div className={s.scroll}>
          <table className={s.table}>
            <caption className={s.srOnly}>Questions to the assistant, newest first</caption>
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Who</th>
                <th scope="col">Asked</th>
                <th scope="col">Answered</th>
                <th scope="col">How</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => {
                const reading = readingOf(x, names);
                const outcome = OUTCOMES.find((o) => o.id === x.outcome);
                return (
                  <tr key={x.id}>
                    <td className={s.when}>{zonedDateTime(x.at, zone)}</td>
                    <td>
                      {x.userName}
                      <small>{x.channel === 'whatsapp' ? 'WhatsApp' : 'Office'}</small>
                    </td>
                    <td>
                      <span className={s.asked}>{x.text}</span> {x.reasked && <Chip tone="warn">asked again</Chip>}
                    </td>
                    <td>
                      {reading && <p className={s.reading}>{`I read that as: ${reading}.`}</p>}
                      <p className={s.reply}>{x.reply ?? '—'}</p>
                    </td>
                    <td>
                      <Chip tone={outcome?.tone}>{outcome?.label ?? x.outcome}</Chip>
                      <small>{`${x.tier ? TIERS[x.tier] : '—'} · ${x.latencyMs} ms`}</small>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {turns.isFetchNextPageError && <ErrorNote error={turns.error} />}
      {turns.hasNextPage && (
        <button type="button" className={`btn ${s.older}`} onClick={() => void turns.fetchNextPage()} disabled={turns.isFetchingNextPage}>
          Show older
        </button>
      )}
      <RefetchNote queries={[turns]} />
    </section>
  );
}

/** The bot's own WhatsApp number (ruling 6; controller amendment #3): what Link WhatsApp tells people to message. */
/**
 * Linked phones (release-2 review, M2): every phone linked to the assistant in the company, by person,
 * and an admin's Remove, which asks first. A person's own Link WhatsApp page removes only their own.
 */
function PhonesTab({ zone }: { zone: string }) {
  const qc = useQueryClient();
  const phones = useAssistantLinkedPhones();
  const [removing, setRemoving] = useState<AssistantLinkedPhoneDto | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/assistant/linked-phones/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.assistantLinkedPhones });
      setRemoving(null);
    },
  });
  const close = () => {
    setRemoving(null);
    remove.reset();
  };
  // By person, in the API's order (by name, then oldest first).
  const people = useMemo(() => {
    const byPerson = new Map<string, { name: string; active: boolean; phones: AssistantLinkedPhoneDto[] }>();
    for (const p of phones.data ?? []) {
      const person = byPerson.get(p.userId) ?? { name: p.userName, active: p.personActive, phones: [] };
      person.phones.push(p);
      byPerson.set(p.userId, person);
    }
    return [...byPerson.entries()];
  }, [phones.data]);
  return (
    <section className={s.section} aria-label="Linked phones">
      <p className={s.rule}>
        Every phone linked to the assistant, by person. Remove one that is lost, or that someone linked who shouldn’t have: it gets no answers from its next message. People link their own
        phone from Link WhatsApp in the app.
      </p>
      {phones.isPending ? (
        <Spinner label="Loading linked phones" />
      ) : phones.isLoadingError ? (
        <LoadFailed error={phones.error} retry={() => void phones.refetch()} />
      ) : people.length === 0 ? (
        <Empty title="No linked phones" description="People link their own phone from Link WhatsApp in the app." />
      ) : (
        people.map(([userId, person]) => (
          <div key={userId} role="group" aria-labelledby={`phones-of-${userId}`} className={s.section}>
            <h3 id={`phones-of-${userId}`} className={s.h}>
              {person.name}
            </h3>
            {!person.active && <p className={s.deactivated}>Deactivated: these phones get no answers.</p>}
            <ul className={s.list}>
              {person.phones.map((p) => (
                <li key={p.id} className={person.active ? s.item : `${s.item} ${s.off}`}>
                  <span className={s.grow}>
                    <b>{p.masked}</b>
                    <small>{`Linked ${zonedDateTime(p.linkedAt, zone)} · ${p.lastUsedAt ? `last used ${zonedDateTime(p.lastUsedAt, zone)}` : 'never used'}`}</small>
                  </span>
                  <button type="button" className="btn sm" onClick={() => setRemoving(p)} aria-label={`Remove ${p.label} from ${person.name}`}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
      <RefetchNote queries={[phones]} />
      <Modal
        open={removing !== null}
        onOpenChange={(open) => (open ? undefined : close())}
        title={removing ? `Remove ${removing.masked} from ${removing.userName}?` : ''}
        footer={
          <>
            <button type="button" className="btn" onClick={close}>
              Cancel
            </button>
            <button type="button" className="btn primary" onClick={() => removing && remove.mutate(removing.id)} disabled={remove.isPending}>
              Remove
            </button>
          </>
        }
      >
        <p className={s.confirmText}>That phone will get no answers until linked again.</p>
        <ErrorNote error={remove.error} />
      </Modal>
    </section>
  );
}

function NumberTab() {
  const qc = useQueryClient();
  const settings = useAssistantSettings();
  const [value, setValue] = useState('');
  const [saved, setSaved] = useState<string | null | undefined>(undefined);
  const helpId = useId();
  const save = useMutation({
    mutationFn: (botPhone: string | null) => api.patch<AssistantSettingsDto>('/assistant/settings', { botPhone }),
    onSuccess: (d, sent) => {
      qc.setQueryData(qk.assistantSettings, d);
      // Only what was saved: a number typed while it was saving stays.
      setValue((v) => (v.trim() === sent ? '' : v));
      setSaved(d.botPhone);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (value.trim()) save.mutate(value.trim());
  };
  const current = settings.data?.botPhone ?? null;
  return (
    <section className={s.section} aria-label="WhatsApp number">
      {settings.isPending ? (
        <Spinner label="Loading the number" />
      ) : settings.isLoadingError ? (
        <LoadFailed error={settings.error} retry={() => void settings.refetch()} />
      ) : (
        <p className={s.line}>{current ? `Staff see +${current} next to their link code.` : 'Not set. Staff are told to message the company’s assistant, without a number.'}</p>
      )}
      <form onSubmit={submit} className={s.row}>
        <label>
          <span className="caps">WhatsApp number</span>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="off"
            className="inp"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setSaved(undefined);
              if (save.isError) save.reset();
            }}
            placeholder="+1 555 123 4567"
            aria-describedby={helpId}
          />
        </label>
        <button type="submit" className="btn primary" disabled={save.isPending || !value.trim()}>
          Save
        </button>
        {current && (
          <button type="button" className="btn" onClick={() => save.mutate(null)} disabled={save.isPending} aria-label="Clear the number">
            Clear
          </button>
        )}
      </form>
      <p id={helpId} className={s.muted}>
        The assistant’s own WhatsApp number, with its country code: 8 to 15 digits, like +1 555 123 4567. Spaces and dashes are fine.
      </p>
      {save.isError && (
        <p role="alert" className="error-note">
          {problemOf(save.error)}
        </p>
      )}
      {saved !== undefined && (
        <p role="status" className={s.ok}>
          {saved ? `Saved: +${saved}` : 'Cleared.'}
        </p>
      )}
      <RefetchNote queries={[settings]} />
    </section>
  );
}
